/**
 * PDF Download Composable
 * Base64 PDF data → Blob → Download
 *
 * 브라우저 atob 는 줄바꿈·data: URL 접두사·패딩 누락에 취약하므로 정규화 후 디코딩한다.
 */

/** data:application/pdf;base64, 제거, 공백 제거, base64 패딩 보정 */
export function normalizePdfBase64(input: string): string {
  if (!input || typeof input !== 'string') return '';
  let s = input.trim();
  const m = /^data:application\/pdf\s*;\s*base64\s*,/i.exec(s);
  if (m) {
    s = s.slice(m[0].length);
  }
  s = s.replace(/\s/g, '');
  // 이미 '=' 패딩이 있어도 길이 % 4 보정 (잘못된 패딩 이중 추가 방지)
  const core = s.replace(/=+$/, '');
  const pad = core.length % 4;
  if (pad !== 0) {
    return core + '='.repeat(4 - pad);
  }
  return core;
}

function decodeBase64ToBinary(normalized: string): string {
  try {
    return atob(normalized);
  } catch {
    // URL-safe base64 (일부 API)
    const std = normalized.replace(/-/g, '+').replace(/_/g, '/');
    return atob(std);
  }
}

function binaryStringToUint8Array(binary: string): Uint8Array {
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** 디코딩 결과가 PDF 헤더(%PDF)로 시작하는지 확인 */
export function looksLikePdf(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  return (
    bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46
  );
}

export interface DownloadPdfOptions {
  /** false 이면 fileName 을 그대로 사용 (확장자 없으면 .pdf 추가) */
  appendDate?: boolean;
}

function sanitizeFileBaseName(name: string): string {
  return String(name || 'receipt').replace(/[/\\?%*:|"<>]/g, '_').trim() || 'receipt';
}

function ensurePdfFileName(name: string): string {
  const n = sanitizeFileBaseName(name);
  return /\.pdf$/i.test(n) ? n : `${n}.pdf`;
}

/**
 * @param base64Data — 순수 base64 또는 data URL
 * @param fileName — 확장자 없이도 됨 (내부에서 .pdf 처리)
 */
export function downloadPdf(base64Data: string, fileName: string, options?: DownloadPdfOptions) {
  const normalized = normalizePdfBase64(base64Data);
  if (!normalized) {
    throw new Error('Empty PDF data');
  }

  let binary: string;
  try {
    binary = decodeBase64ToBinary(normalized);
  } catch {
    throw new Error('Invalid base64 PDF data');
  }

  const bytes = binaryStringToUint8Array(binary);
  if (!looksLikePdf(bytes)) {
    console.warn('[downloadPdf] Decoded bytes do not start with %PDF- header; file may still download.');
  }

  const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const appendDate = options?.appendDate !== false;
  link.download = appendDate
    ? `${sanitizeFileBaseName(fileName)}_${new Date().toISOString().split('T')[0]}.pdf`
    : ensurePdfFileName(fileName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
